import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown, Scale } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/utils/cn'
import { sectionsOf, type NavGroup, type NavItem, type NavigationModel } from '@/lib/navigation'
import { useRecentClients } from '@/lib/recent-clients'

interface AppSidebarProps {
  model: NavigationModel
  /** Caminho do item ativo (ver `activeHref`). */
  active: string | null
}

const itemClass = (active: boolean) =>
  cn(
    'flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors',
    active ? 'bg-accent text-accent-foreground' : 'text-foreground/80 hover:bg-muted hover:text-foreground'
  )

function MainLink({ item, active }: { item: NavItem; active: boolean }) {
  const Icon = item.icon
  return (
    <Link to={item.to} className={itemClass(active)} aria-current={active ? 'page' : undefined}>
      {Icon && <Icon className="size-5" />}
      <span className="flex-1">{item.label}</span>
    </Link>
  )
}

function SubLink({ item, active }: { item: NavItem; active: boolean }) {
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'block rounded px-3 py-2 text-sm',
        active ? 'bg-accent font-medium text-accent-foreground' : 'text-foreground/70 hover:bg-muted hover:text-foreground'
      )}
    >
      {item.label}
    </Link>
  )
}

/** Clientes abertos por último, logo abaixo de "Clientes": voltar ao dossiê em um clique. */
function RecentClientLinks() {
  const recent = useRecentClients()
  const { pathname } = useLocation()
  if (recent.length === 0) return null
  return (
    <div aria-label="Clientes recentes" role="group" className="mb-1 ml-4 flex flex-col gap-0.5 border-l-2 border-border pl-4">
      {recent.map((client) => {
        const to = `/clients/${client.id}`
        const current = pathname === to || pathname.startsWith(`${to}/`)
        return (
          <Link
            key={client.id}
            to={to}
            aria-current={current ? 'page' : undefined}
            className={cn(
              'truncate rounded px-3 py-1.5 text-sm',
              current ? 'bg-accent font-medium text-accent-foreground' : 'text-foreground/70 hover:bg-muted hover:text-foreground'
            )}
          >
            {client.name}
          </Link>
        )
      })}
    </div>
  )
}

/** Visões gerais: acesso direto, sem passar pelo cliente, com menos destaque que Clientes. */
function OverviewSection({ group, active }: { group: NavGroup; active: string | null }) {
  const [open, setOpen] = useState(true)
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-4">
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground">
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-0.5">
        {group.items.map((item) => {
          const Icon = item.icon
          const current = item.to === active
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={current ? 'page' : undefined}
              className={cn(
                'flex items-center gap-3 rounded-lg px-4 py-1.5 text-sm transition-colors',
                current ? 'bg-accent font-medium text-accent-foreground' : 'text-foreground/70 hover:bg-muted hover:text-foreground'
              )}
            >
              {Icon && <Icon className="size-4" />}
              <span className="flex-1">{item.label}</span>
            </Link>
          )
        })}
      </CollapsibleContent>
    </Collapsible>
  )
}

function NavGroupSection({ group, active }: { group: NavGroup; active: string | null }) {
  const containsActive = group.items.some((i) => i.to === active)
  const [open, setOpen] = useState(containsActive)
  // Navegar para uma tela do grupo abre o grupo (sem impedir de fechá-lo depois).
  useEffect(() => {
    if (containsActive) setOpen(true)
  }, [containsActive])
  const Icon = group.icon
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className={cn(itemClass(containsActive && !open), 'w-full')}>
        <Icon className="size-5" />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1 ml-4 flex flex-col gap-1 border-l-2 border-border pl-4">
        {sectionsOf(group.items).map((section, index) => (
          <div key={section[0].to} className="flex flex-col gap-1">
            {index > 0 && <Separator className="my-1" />}
            {section.map((item) => <SubLink key={item.to} item={item} active={item.to === active} />)}
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}

/** Logo + menu lateral (o mesmo conteúdo no desktop e na gaveta do celular). */
export function AppSidebar({ model, active }: AppSidebarProps) {
  return (
    <>
      <div className="flex h-16 items-center border-b border-border px-4">
        <Scale className="mr-3 size-8 text-primary" />
        <div className="flex flex-col">
          <span className="text-lg font-bold text-foreground">LawPG</span>
          <span className="text-xs text-muted-foreground">Sistema Jurídico</span>
        </div>
      </div>
      <nav aria-label="Menu principal" className="flex flex-col gap-1 p-4">
        {model.main.map((item) => (
          <div key={item.to} className="flex flex-col gap-1">
            <MainLink item={item} active={item.to === active} />
            {item.to === '/clients' && <RecentClientLinks />}
          </div>
        ))}
        {model.groups.filter((g) => g.key === 'visoes').map((group) => <OverviewSection key={group.key} group={group} active={active} />)}
        <div className="mt-4 flex flex-col gap-1">
          {model.groups.filter((g) => g.key !== 'visoes').map((group) => <NavGroupSection key={group.key} group={group} active={active} />)}
        </div>
      </nav>
    </>
  )
}
